import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatError } from '@angular/material/form-field';
import { TranslationService } from 'src/app/services/translation.service';
import { TripType } from '../../../interfaces/register-user.response';
import { RegisterUserService, RegisterUserFormService } from '../../../services';
import { BaseFormField } from '../../form/abstracts';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { BreakpointsService } from 'src/app/services/breakpoints.service';

@Component({
  selector: 'app-trip-types-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: TripTypesFieldComponent
    },
  ],
  templateUrl: './trip-types-field.component.html',
  styleUrls: ['./trip-types-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripTypesFieldComponent extends BaseFormField<TripType[]> {
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  public isNextButtonClicked: InputSignal<boolean> = input<boolean>();
  
  private _cdr = inject(ChangeDetectorRef);
  private _translationService = inject(TranslationService);
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _breakpointsService = inject(BreakpointsService);
  
  private _formControlName = computed(() => this.form().get(this.formControlName()));
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _tripTypeList = this._registerUserService.registerUser().travelerType;
  private _currentTripTypeId = this._registerUserFormService.tripTypeId;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isMobile = this._breakpointsService.isMobile;
  
  // ! PARA CICERONEWEB-SSR NO HARÁ FALTA SETEARLO POR EFFECT SEGÚN 
  // ! EL IDIOMA YA QUE CUANDO SE CAMBIA DE IDIOMA RECARGA LA PÁGINA
  // ! VIENE POR URL
  // ! EMT QUIERE ESTE ORDEN HARCODEADO: 1.Familiar, 2.Romántico, 3.Mujeres, 4.Senior, 5.LGTBI, 6.Solteros, 7.Mochilero
  // ! DEJO COMENTADO POR ORDEN ALFABÉTICO
  protected tripTypeListChips = signal<TripType[]>([]);
  //setTripTypeList = effect(() => {
  //  this.tripTypeListChips = this._tripTypeList
  //    .map(type => {
  //      return {
  //        id: type.id,
  //        value: type.id,
  //        label: type.name[this._selectedLanguage()],
  //      iconName: type.icon,
  //    }
  //  })
  //  .sort((a, b) => a.label.localeCompare(b.label));
  //}, { allowSignalWrites: true });
  setTripTypeList = effect(() => {
    // EMT Ordering logic
    const eMTOrder: { [key: string]: number } = {
      'Familiar': 1,
      'Romántico': 2,
      'Mujeres': 3,
      'Senior': 4,
      'LGTBI': 5,
      'Solteros': 6,
      'Mochilero': 7
    };

    this.tripTypeListChips.set(this._tripTypeList
      .map(type => {
        return {
          id: type.id,
          value: type.id,
          label: type.name[this._selectedLanguage()],
          iconName: type.icon,
        }
      })
      .sort((a, b) => {
        // EMT Ordering logic
        const orderA = eMTOrder[a.label] || 999; 
        const orderB = eMTOrder[b.label] || 999;
        
        return orderA - orderB;
      })
    );
  }, { allowSignalWrites: true });

  markAllFieldsAsTouchedForDesktop = effect(() => {
    if (this._markAllFormFieldsAsTouched()) {
      const control = this._formControlName();

      control?.markAsTouched();
      control?.markAsDirty();

      // Triggear change detection manually for OnPush
      this._cdr.detectChanges();
    }
  }, { allowSignalWrites: true });

  public handleChipSetTripTypeChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: TripType }>;
    const selectedTripTypes = customEvent.detail.values[0]; // is single selection
    this._registerUserFormService.setTripType(selectedTripTypes);
    
    const control = this._formControlName();
    control?.setValue(selectedTripTypes);
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      closeAllCiSnackbar();
    }
  }
  
  public isSelectedTripType(id: number): boolean {
    return this._currentTripTypeId() 
      ? this._currentTripTypeId() === id
      : false;
  }
  
  public isInvalidField(): boolean {
    const control = this._formControlName();
    if (!control?.hasError('required')) return false;
    
    // En mobile: solo mostrar error si el control específico ha sido tocado
    // Esto evita que errores de slides anteriores afecten a este campo
    if (this._isMobile()) {
      return (control.touched || control.dirty) && this.isNextButtonClicked();
    }
    
    // En desktop: comportamiento normal
    return control.touched || control.dirty;
  }
}